<template>
  <div class="tracking-management">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame :title="$t('tracking')">
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <AdvertiserFilters @filter="onFilter" @clearFilters="onClearFilters" id="advertiser-filters">
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="columns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </AdvertiserFilters>
        <table-base
          has-phone-layout
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :columns="columns"
          :items="advertisers"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectAdvertiser"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope" >
            <template v-if="scope.cell.column.value === 'platforms'">
              <div class="d-flex flex-wrap gap-6">
                <PlatformPanel
                  v-for="platform in scope.cell.item.platforms"
                  :key="platform.code"
                  :country="platform"
                />
              </div>
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import AdvertiserFilters from '../../components/forms/filters/TrackingAdvertisers';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
  import { isStr, extractNestedProp, isObj } from '../../utils';
  import columns from '../../config/tracking/columnsAdvertisers';
  import PlatformPanel from '@/components/panels/Platform.vue';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      AdvertiserFilters,
      PlatformPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'asc',
          page: 1,
          rowsPerPage: 15,
          sortBy: 'title',
          totalItems: 0,
        },
        totalPages: 0,
        query: {},
        isLoading: false,
        isExporting: false,
      };
    },
    computed: {
      addAction() {
        return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
      },
      headerActions() {
        return [
          this.$ability.can('create', 'clicky-exports') && {
            key: 'export',
            icon: 'cloud_upload',
            title: this.$t('export_clicky_config'),
            isDisabled: this.isExporting,
            isLoading: this.isExporting,
            onClick: this.exportClickyConfig,
          },
          {
            key: 'bulk-upload',
            icon: 'upload',
            variant: 'outlined',
            title: this.$t('bulk_upload'),
            onClick: this.openBulkUploadModal,
          },
          {
            key: 'add',
            title: this.$t('new_advertiser'),
            onClick: this.openNewAdvertiserModal,
          },
        ].filter(Boolean);
      },
      advertisers() {
        return this.$store.getters['tracking/advertisers'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.isLoading = true;
        this.getAdvertisers(this.query);
      },
      openBulkUploadModal() {
        this.$router.push({ name: 'tracking-management-bulk-upload', query: this.$route.query });
      },
      exportClickyConfig() {
        this.isExporting = true;

        return this.$store
          .dispatch('tracking/exportClickyConfig')
          .catch((err) => err)
          .finally(() => {
            this.isExporting = false;
          });
      },
      selectAdvertiser(advertiser) {
        this.$store.dispatch('tracking/setActiveAdvertiser', advertiser);
        this.$router.push({ name: 'tracking-management-advertiser', params: { id: advertiser.id }, query: this.$route.query });
      },
      async getAdvertisers(query) {
        this.isLoading = true;
        query.per_page = this.pagination.rowsPerPage;

        return this.$store
          .dispatch('tracking/getAdvertisers', query)
          .then((res) => {
            const { advertisersMeta } = res;
            const { total, current_page, last_page } = advertisersMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;

            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.runInitialFetch(() => this.getAdvertisers(nextQuery));
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getAdvertisers(nextQuery)
          .then(() => {
            // scroll to top
            const target = document.querySelector('#advertiser-filters');

            if (!isObj(target)) {
              return false;
            }

            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          })
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };

        this.runInitialFetch(() => this.getAdvertisers(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, page: 1 };

        this.getAdvertisers(this.query);
      },
      openNewAdvertiserModal() {
        this.$router.push({ name: 'tracking-management-new-advertiser', query: this.$route.query });
      },
      openEditAdvertiserModal(id) {
        this.$router.push({ name: 'tracking-management-advertiser', params: { id }, query: this.$route.query });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
    // todo: change this when merging with the vue 3 branch
    metaInfo() {
      return {
        title: this.$t('tracking_management'),
      };
    },
  };
</script>

<style lang="scss">
</style>
